import {
  Header,
  SpaceBetween,
  FormField,
  Container,
  Input,
  Textarea,
  Select,
  SelectProps,
  Button,
  Box,
  StatusIndicator,
  TextContent,
  Alert,
  ColumnLayout,
  Flashbar,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useCallback, useEffect } from 'react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { EventStepProps } from './components/Common';
import { LocationComponent } from './components/LocationComponent';
import { VisibilityComponent } from './components/VisibilityComponent';
import { AciGrantComponent } from './components/AciGrantComponent';
import { CommunityOrganizationComponent } from './components/CommunityOrganizationComponent';
import { ShiftDetailsComponent } from './components/ShiftDetailsComponent';
import { AdvancedSignupComponent } from './components/AdvancedSignupComponent';
import { CoordinatorsPartnersComponent } from './components/CoordinatorsPartnersComponent';
import { ContactsComponent } from './components/ContactsComponent';
import { EventInput, ShiftInput, ShiftTimingType } from '@amzn/red-velvet-api';
import { findOption, formatCharacterCount } from '../../../utils/eventUtils';

export const getEventDetailsStep = (
  title: string,
  description: string,
  currentFields: EventInput,
  updateFieldHandler: (fields: EventInput) => void,
  metricPrefix: string,
  errors?: Record<string, string>,
  onGrantInfoClick?: () => void
) => {
  return {
    title: title,
    description: description,
    content: (
      <SpaceBetween direction="vertical" size="l">
        <BasicInfoContent
          currentFields={currentFields}
          updateFieldHandler={updateFieldHandler}
          errors={errors}
          metricPrefix={metricPrefix}
        />
        <AdditionalInfoContent
          currentFields={currentFields}
          updateFieldHandler={updateFieldHandler}
          errors={errors}
          onGrantInfoClick={onGrantInfoClick}
          metricPrefix={metricPrefix}
        />
      </SpaceBetween>
    ),
  };
};
export const getPublishStep = (
  title: string,
  previewText: string,
  openPreviewModal: () => void,
  reviewAndPublishStepProps: ReviewAndPublishStepProps,
  errorText?: string
) => {
  const result = {
    title: title,
    errorText,
    info: (
      <Button data-testid="preview-btn" onClick={openPreviewModal}>
        {previewText}
      </Button>
    ),
    content: (
      <SpaceBetween direction="vertical" size="l">
        {ReviewAndPublishContent(reviewAndPublishStepProps)}
      </SpaceBetween>
    ),
  };
  return result;
};

/////////////////////////////////////////////////////////////////////////////
/////////////////// Event Detail Steps Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

const EVENT_TITLE_INPUT_MAX_CHAR = 255;
const EVENT_DESCRIPTION_INPUT_MAX_CHAR = 65536;
const EVENT_DESCRIPTION_MAX_HEIGHT_PX = 500;
const EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR = 100;

function BasicInfoContent(props: EventStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const { currentFields, updateFieldHandler, errors, isReadOnly } = props;

  const [eventTitle, setEventTitle] = useState(currentFields.title ?? '');
  const [eventDescription, setEventDescription] = useState(currentFields.description ?? '');
  const [summarizedDescription, setSummarizedDescription] = useState(
    currentFields.shortDescription ?? ''
  );
  const [titleClearedWarning, setTitleClearedWarning] = useState(false);
  const lastCommittedTitle = useRef(currentFields.title ?? '');
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Workaround to support auto-grow: Cloudscape Textarea does not expose
    // a public API for dynamic height, so we query its internal <textarea> element.
    const textarea = descriptionRef.current?.querySelector('textarea');
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, EVENT_DESCRIPTION_MAX_HEIGHT_PX)}px`;
    textarea.style.overflow =
      textarea.scrollHeight > EVENT_DESCRIPTION_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, [eventDescription]);

  const updateTextField = () => {
    if (eventTitle.trim() === '' && lastCommittedTitle.current !== '') {
      setEventTitle(lastCommittedTitle.current);
      setTitleClearedWarning(true);
      return;
    }
    setTitleClearedWarning(false);
    lastCommittedTitle.current = eventTitle;
    updateFieldHandler({
      ...currentFields,
      title: eventTitle,
      description: eventDescription,
      shortDescription: summarizedDescription,
    });
  };

  return (
    <Container header={<Header variant="h2">{t('eventDetailsStep.basicInformation')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.eventTitle')}
          description={t('eventDetailsStep.eventTitleSubText')}
          constraintText={`${t('eventDetailsStep.eventTitleConstraint', { maxLength: EVENT_TITLE_INPUT_MAX_CHAR })} ${formatCharacterCount(eventTitle.length, EVENT_TITLE_INPUT_MAX_CHAR)}`}
          errorText={
            titleClearedWarning
              ? t('error.cannotClearEventTitle')
              : (errors?.eventTitle ??
                (eventTitle?.length > EVENT_TITLE_INPUT_MAX_CHAR
                  ? t('error.inputTooLong', { maxLength: EVENT_TITLE_INPUT_MAX_CHAR })
                  : ''))
          }
        >
          <Input
            placeholder={t('eventDetailsStep.eventTitlePlaceholder')}
            onChange={(event) => {
              setEventTitle(event.detail.value);
              setTitleClearedWarning(false);
            }}
            onBlur={() => updateTextField()}
            value={eventTitle}
            readOnly={isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.eventDescription')}
          description={t('eventDetailsStep.eventDescriptionSubText')}
          constraintText={`${t('eventDetailsStep.eventDescriptionConstraint', { maxLength: EVENT_DESCRIPTION_INPUT_MAX_CHAR })} ${formatCharacterCount(eventDescription.length, EVENT_DESCRIPTION_INPUT_MAX_CHAR)}`}
          errorText={
            errors?.eventDescription ??
            (eventDescription?.length > EVENT_DESCRIPTION_INPUT_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: EVENT_DESCRIPTION_INPUT_MAX_CHAR })
              : '')
          }
        >
          <div ref={descriptionRef}>
            <Textarea
              onChange={(event) => {
                setEventDescription(event.detail.value);
              }}
              onBlur={() => updateTextField()}
              value={eventDescription}
              placeholder={t('eventDetailsStep.eventDescriptionPlaceholder')}
              readOnly={isReadOnly}
            />
          </div>
        </FormField>
        <FormField
          label={t('eventDetailsStep.summarizedDescription')}
          description={t('eventDetailsStep.summarizedDescriptionSubText')}
          constraintText={`${t('eventDetailsStep.summarizedDescriptionConstraint', { maxLength: EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR })} ${formatCharacterCount(summarizedDescription.length, EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR)}`}
          errorText={
            errors?.summarizedDescription ??
            (summarizedDescription?.length > EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR })
              : '')
          }
        >
          <Textarea
            onChange={(event) => {
              setSummarizedDescription(event.detail.value);
            }}
            onBlur={() => updateTextField()}
            value={summarizedDescription}
            placeholder={t('eventDetailsStep.summarizedDescriptionPlaceholder')}
            readOnly={isReadOnly}
          />
        </FormField>
        <CommunityOrganizationComponent {...props} />
        <LocationComponent {...props} />
        <VisibilityComponent {...props} />
      </SpaceBetween>
    </Container>
  );
}

function AdditionalInfoContent(props: EventStepProps & { onGrantInfoClick?: () => void }) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const focusOptions = t('focus', { returnObjects: true }) as SelectProps.Options;
  const primaryActivityOptions = t('activity', { returnObjects: true }) as SelectProps.Options;

  const [focusArea, setFocusArea] = useState<OptionDefinition | null>(
    props.currentFields.focusArea ? findOption(focusOptions, props.currentFields.focusArea) : null
  );
  const [primaryActivity, setPrimaryActivity] = useState<OptionDefinition | null>(
    props.currentFields.primaryActivity
      ? findOption(primaryActivityOptions, props.currentFields.primaryActivity)
      : null
  );

  return (
    <Container header={<Header variant="h2">{t('eventDetailsStep.additionalInfo')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.focusArea')}
          description={t('eventDetailsStep.focusAreaSubText')}
          errorText={props.errors?.focusArea}
        >
          <Select
            onChange={(event) => {
              setFocusArea(event.detail.selectedOption);
              props.updateFieldHandler({
                ...props.currentFields,
                focusArea: event.detail.selectedOption.value ?? '',
              });
            }}
            selectedOption={focusArea}
            options={focusOptions}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.primaryActivity')}
          description={t('eventDetailsStep.primaryActivitySubText')}
          errorText={props.errors?.primaryActivity}
        >
          <Select
            onChange={(event) => {
              setPrimaryActivity(event.detail.selectedOption);
              props.updateFieldHandler({
                ...props.currentFields,
                primaryActivity: event.detail.selectedOption.value ?? '',
              });
            }}
            selectedOption={primaryActivity}
            options={primaryActivityOptions}
            placeholder={t('eventDetailsStep.primaryActivityPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <AciGrantComponent {...props} onGrantInfoClick={props.onGrantInfoClick} />
      </SpaceBetween>
    </Container>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Shift Details Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

export interface ShiftStepProps {
  currentFields: ShiftInput[];
  updateFieldHandler: (fields: ShiftInput[], changedIndex?: number, removedIndex?: number) => void;
  eventFields: EventInput;
  updateEventFieldHandler: (fields: EventInput) => void;
  errors?: Record<string, string>;
  isReadOnly?: boolean;
  shiftIdsWithSignups?: Set<string>;
  onShiftTypeInfoClick?: () => void;
  metricPrefix: string;
}

export const getShiftStep = (
  title: string,
  description: string,
  currentFields: ShiftInput[],
  updateFieldHandler: (fields: ShiftInput[], changedIndex?: number, removedIndex?: number) => void,
  eventFields: EventInput,
  updateEventFieldHandler: (fields: EventInput) => void,
  metricPrefix: string,
  errors?: Record<string, string>,
  shiftIdsWithSignups?: Set<string>,
  onShiftTypeInfoClick?: () => void
) => {
  return {
    title,
    description,
    content: (
      <ShiftStepContent
        currentFields={currentFields}
        updateFieldHandler={updateFieldHandler}
        eventFields={eventFields}
        updateEventFieldHandler={updateEventFieldHandler}
        errors={errors}
        shiftIdsWithSignups={shiftIdsWithSignups}
        onShiftTypeInfoClick={onShiftTypeInfoClick}
        metricPrefix={metricPrefix}
      />
    ),
  };
};

function ShiftStepContent({
  currentFields,
  updateFieldHandler,
  eventFields,
  updateEventFieldHandler,
  errors,
  isReadOnly,
  shiftIdsWithSignups,
  onShiftTypeInfoClick,
  metricPrefix,
}: ShiftStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const [allExpanded, setAllExpanded] = useState(true);
  const [expandToggleKey, setExpandToggleKey] = useState(0);

  const nextId = useRef(currentFields.length);
  const shiftKeys = useRef<number[]>(currentFields.map((_, i) => i));

  const getKey = (index: number): number => {
    while (shiftKeys.current.length <= index) {
      shiftKeys.current.push(nextId.current++);
    }
    return shiftKeys.current[index];
  };

  const updateShift = (index: number, updated: ShiftInput) => {
    const newShifts = [...currentFields];
    newShifts[index] = updated;
    updateFieldHandler(newShifts, index);
  };

  const removeShift = (index: number) => {
    shiftKeys.current.splice(index, 1);
    const newShifts = currentFields.filter((_, i) => i !== index);
    updateFieldHandler(newShifts, undefined, index);
  };

  const addShift = () => {
    const midnight = new Date(new Date().toDateString());
    updateFieldHandler([
      ...currentFields,
      {
        startDateTime: midnight,
        endDateTime: midnight,
        useEventLocation: true,
        shiftTimingType: ShiftTimingType.SCHEDULED,
        shiftLength: 0,
      },
    ]);
  };

  const toggleAll = () => {
    setAllExpanded((prev) => !prev);
    setExpandToggleKey((prev) => prev + 1);
  };

  const fieldsRef = useRef(currentFields);
  fieldsRef.current = currentFields;
  const handlerRef = useRef(updateFieldHandler);
  handlerRef.current = updateFieldHandler;

  const eventFieldsRef = useRef(eventFields);
  eventFieldsRef.current = eventFields;
  const eventHandlerRef = useRef(updateEventFieldHandler);
  eventHandlerRef.current = updateEventFieldHandler;

  const stableUpdateEventFields = useCallback((overrides: EventInput) => {
    eventHandlerRef.current({ ...eventFieldsRef.current, ...overrides });
  }, []);

  return (
    <Box>
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <Button variant="normal" onClick={toggleAll}>
                  {allExpanded ? t('shiftStep.collapseAll') : t('shiftStep.expandAll')}
                </Button>
              }
            >
              {t('shiftStep.eventShifts')}
            </Header>
          }
        >
          <SpaceBetween size="l">
            {currentFields.map((shift, index) => (
              <ShiftDetailsComponent
                key={getKey(index)}
                shift={shift}
                index={index}
                updateShift={updateShift}
                onRemove={
                  currentFields.length > 1 &&
                  !(shift.shiftId && shiftIdsWithSignups?.has(shift.shiftId))
                    ? removeShift
                    : undefined
                }
                errors={errors}
                expandOverride={allExpanded}
                expandToggleKey={expandToggleKey}
                isReadOnly={isReadOnly}
                eventFields={eventFields}
                onShiftTypeInfoClick={onShiftTypeInfoClick}
                metricPrefix={metricPrefix}
              />
            ))}
            {!isReadOnly && (
              <Button
                iconName="add-plus"
                variant="link"
                onClick={addShift}
                data-aci-analytics-name={`${metricPrefix}_shiftAdded`}
              >
                {t('shiftStep.addShift')}
              </Button>
            )}
          </SpaceBetween>
        </Container>
        <AdvancedSignupComponent
          eventFields={eventFields}
          updateEventFields={stableUpdateEventFields}
          isReadOnly={isReadOnly}
          metricPrefix={metricPrefix}
        />
      </SpaceBetween>
    </Box>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Team & Partnership Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

export const getTeamAndPartnershipStep = (
  title: string,
  description: string,
  currentFields: EventInput,
  updateFieldHandler: (fields: EventInput) => void,
  metricPrefix: string,
  errors?: Record<string, string>
) => {
  return {
    title,
    description,
    content: (
      <TeamAndPartnershipStepContent
        currentFields={currentFields}
        updateFieldHandler={updateFieldHandler}
        errors={errors}
        metricPrefix={metricPrefix}
      />
    ),
    isOptional: false,
  };
};

function TeamAndPartnershipStepContent(props: EventStepProps) {
  return (
    <SpaceBetween size="l">
      <CoordinatorsPartnersComponent {...props} />
      <ContactsComponent {...props} />
    </SpaceBetween>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Review And Publish Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

interface ReviewAndPublishStepProps {
  currentEventFields: EventInput;
  currentShiftFields: ShiftInput[];
  updateEventFieldHandler: (fields: EventInput) => void;
  updateShiftFieldHandler: (
    fields: ShiftInput[],
    changedIndex?: number,
    removedIndex?: number
  ) => void;
  setActiveStepIndex: (index: number) => void;
  errors?: Record<string, string>;
  publicationStatus?: 'draft' | 'pending' | 'published' | 'unpublished';
  totalSignupCount?: number;
  submitError?: string | null;
  onDismissError?: () => void;
  onDismissPublishError?: () => void;
  metricPrefix: string;
}

function ReviewAndPublishContent(props: ReviewAndPublishStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const {
    currentEventFields,
    currentShiftFields,
    updateShiftFieldHandler,
    updateEventFieldHandler,
    errors,
    setActiveStepIndex,
  } = props;

  return (
    <Box>
      <SpaceBetween size="xxl">
        {/* Event Details Section */}
        <SpaceBetween size="xs" data-testid="step-1-review">
          <Container>
            <ColumnLayout
              columns={2}
              borders={(props.totalSignupCount ?? 0) > 0 ? 'vertical' : undefined}
            >
              <SpaceBetween size="xxxs">
                <TextContent>
                  <b>{t('reviewPublishStep.publication')}</b>
                </TextContent>
                {props.publicationStatus === 'published' ? (
                  <StatusIndicator type="success">
                    {t('reviewPublishStep.published')}
                  </StatusIndicator>
                ) : props.publicationStatus === 'pending' ? (
                  <StatusIndicator type="warning">
                    {t('reviewPublishStep.pendingChanges')}
                  </StatusIndicator>
                ) : props.publicationStatus === 'unpublished' ? (
                  <StatusIndicator type="stopped">
                    {t('reviewPublishStep.unpublished')}
                  </StatusIndicator>
                ) : (
                  <StatusIndicator type="in-progress">
                    {t('reviewPublishStep.draft')}
                  </StatusIndicator>
                )}
              </SpaceBetween>
              {(props.totalSignupCount ?? 0) > 0 && (
                <SpaceBetween size="xxxs">
                  <TextContent>
                    <b>{t('reviewPublishStep.signups')}</b>
                  </TextContent>
                  <TextContent>
                    {t('reviewPublishStep.signupCount', { count: props.totalSignupCount })}
                  </TextContent>
                </SpaceBetween>
              )}
            </ColumnLayout>
          </Container>
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="event-edit-step-btn" onClick={() => setActiveStepIndex(0)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('eventDetailsStep.eventDetails')}
          </Header>
          <BasicInfoContent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
          <AdditionalInfoContent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
        </SpaceBetween>
        {/* Shift Details Section */}
        <SpaceBetween size="xs" data-testid="step-2-review">
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="shift-edit-step-btn" onClick={() => setActiveStepIndex(1)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('shiftStep.stepTitle')}
          </Header>
          <ShiftStepContent
            currentFields={currentShiftFields}
            updateFieldHandler={updateShiftFieldHandler}
            eventFields={currentEventFields}
            updateEventFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
        </SpaceBetween>
        {/* Team & Partnership Details Section */}
        <SpaceBetween size="xs" data-testid="step-3-review">
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="team-edit-step-btn" onClick={() => setActiveStepIndex(2)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('teamPartnershipStep.stepTitle')}
          </Header>
          <CoordinatorsPartnersComponent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
          <ContactsComponent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
        </SpaceBetween>
        {props.errors?.publishError !== undefined && (
          <Alert
            type="error"
            header={t('error.publishErrorHeader')}
            dismissible={!!props.onDismissPublishError}
            onDismiss={props.onDismissPublishError}
          >
            {props.errors.publishError}
          </Alert>
        )}
        {props.submitError && (
          <Flashbar
            items={[
              {
                type: 'error',
                header: t('error.publishFailedHeader'),
                content: props.submitError,
                dismissible: !!props.onDismissError,
                onDismiss: props.onDismissError,
              },
            ]}
          />
        )}
      </SpaceBetween>
    </Box>
  );
}
